import { createSelector, createSlice, PayloadAction } from "@reduxjs/toolkit"
import { AllowedEntityNames } from "../constants"
import { FlightLayersUiContext } from "./types"
import { memoize } from "nodash"
import { HotspotProperties } from "@nova/constants"
import { RootState } from "integrations/redux"

const initialLayersUiContext = {
    flightPath: false,
    hotspotArea: false,
    hotspotHeatmap: false,
    hotspotLabels: false,
    hotspotPoints: false,
    hotspotHexmap: false,
    hotspotPolygons: false,
    hotspotScanArea: false,
    hotspotOrthoOverlaps: false,
    removedHotspots: false,
}

const initialState = {
    byId: {} as Record<string, { uiContext: { showLayers: typeof initialLayersUiContext } }>,
    propertiesById: {} as Record<string, HotspotProperties>,
    fetching: [],
    starredAtlasLiveVideoIds: [] as string[],
}

const updateLayersUiContext = (
    state: typeof initialState,
    entityId: string,
    newContext: FlightLayersUiContext
) => {
    state.byId[entityId] ??= { uiContext: { showLayers: { ...initialLayersUiContext } } }

    const oldContext = state.byId[entityId].uiContext.showLayers || initialLayersUiContext
    state.byId[entityId].uiContext.showLayers = {
        ...oldContext,
        ...newContext,
    }
}

const updateUiLayerActivationStatus =
    (layerNames: string[], isActivated?: boolean) =>
    (state: typeof initialState, action: PayloadAction<{ id: string }>) => {
        const { id } = action.payload

        layerNames.forEach((layerName) =>
            updateLayersUiContext(state, id, { [layerName]: isActivated } as FlightLayersUiContext)
        )
    }

export const slice = createSlice({
    name: AllowedEntityNames.Flights,
    initialState: { ...initialState },
    reducers: {
        deactivatedFlightPath: (
            state,
            action: PayloadAction<{ flightId: string; autoZoom?: boolean }>
        ) => {
            const { flightId } = action.payload

            updateLayersUiContext(state, flightId, { flightPath: false } as FlightLayersUiContext)
        },
        activateHotspotPointLayer: updateUiLayerActivationStatus(["hotspotPoints"], true),
        deactivateHotspotPointLayer: updateUiLayerActivationStatus(
            ["hotspotPoints", "hotspotLabels"],
            false
        ),
        activateHotspotLabelsLayer: updateUiLayerActivationStatus(
            ["hotspotLabels", "hotspotPoints"],
            true
        ),
        deactivateHotspotLabelsLayer: updateUiLayerActivationStatus(["hotspotLabels"], false),
        activateHotspotOrthoLayer: updateUiLayerActivationStatus(["hotspotOrthos"], true),
        deactivateHotspotOrthoLayer: updateUiLayerActivationStatus(["hotspotOrthos"], false),
        activateHotspotOrthoOverlapLayer: updateUiLayerActivationStatus(
            ["hotspotOrthoOverlaps"],
            true
        ),
        deactivateHotspotOrthoOverlapLayer: updateUiLayerActivationStatus(
            ["hotspotOrthoOverlaps"],
            false
        ),
        disableHotspotOrthoOverlapLayer: updateUiLayerActivationStatus(
            ["hotspotOrthoOverlaps"],
            undefined
        ),
        activateHotspotPolygonLayer: updateUiLayerActivationStatus(["hotspotPolygons"], true),
        deactivateHotspotPolygonLayer: updateUiLayerActivationStatus(["hotspotPolygons"], false),
        activateHotspotScanAreaLayer: updateUiLayerActivationStatus(["hotspotScanArea"], true),
        deactivateHotspotScanAreaLayer: updateUiLayerActivationStatus(["hotspotScanArea"], false),
        activateOrthoScanAreaLayer: updateUiLayerActivationStatus(["orthoScanArea"], true),
        deactivateOrthoScanAreaLayer: updateUiLayerActivationStatus(["orthoScanArea"], false),
        activateHotspotPolygonAreaLayer: updateUiLayerActivationStatus(["hotspotArea"], true),
        deactivateHotspotPolygonAreaLayer: updateUiLayerActivationStatus(["hotspotArea"], false),
        setHotspotOverlapLayer: (
            state: typeof initialState,
            action: PayloadAction<{ id: string; active: boolean }>
        ) => {
            const { active, id: flightId } = action.payload
            updateLayersUiContext(state, flightId, {
                hotspotOrthoOverlaps: active,
            } as FlightLayersUiContext)
        },
        activateRemovedHotspotsLayer: updateUiLayerActivationStatus(["removedHotspots"], true),
        deactivateRemovedHotspotsLayer: updateUiLayerActivationStatus(["removedHotspots"], false),
        clearFlightsState: () => {
            return { ...initialState }
        },
        starAtlasLiveVideoFlight: (state, action: { payload: string }) => {
            if (!state.starredAtlasLiveVideoIds.includes(action.payload)) {
                state.starredAtlasLiveVideoIds.push(action.payload)
            }
        },
        starManyAtlasLiveVideoFlights: (state, action: { payload: string[] }) => {
            state.starredAtlasLiveVideoIds = Array.from(
                new Set([...state.starredAtlasLiveVideoIds, ...action.payload])
            )
        },
        unstarAtlasLiveVideoFlight: (state, action: { payload: string }) => {
            state.starredAtlasLiveVideoIds = state.starredAtlasLiveVideoIds.filter(
                (f) => f !== action.payload
            )
        },
        unstarAllAtlasLiveVideoFlights: (state) => {
            state.starredAtlasLiveVideoIds = []
        },
    },
})

const { actions, reducer } = slice

export const {
    deactivatedFlightPath: deactivatedFlightPathAction,
    activateHotspotPointLayer: activateHotspotPointLayerAction,
    deactivateHotspotPointLayer: deactivateHotspotPointLayerAction,
    activateHotspotLabelsLayer: activateHotspotLabelsLayerAction,
    deactivateHotspotLabelsLayer: deactivateHotspotLabelsLayerAction,
    activateHotspotOrthoLayer: activateHotspotOrthoLayerAction,
    deactivateHotspotOrthoLayer: deactivateHotspotOrthoLayerAction,
    activateHotspotOrthoOverlapLayer: activateHotspotOrthoOverlapLayerAction,
    deactivateHotspotOrthoOverlapLayer: deactivateHotspotOrthoOverlapLayerAction,
    disableHotspotOrthoOverlapLayer: disableHotspotOrthoOverlapLayerAction,
    activateHotspotPolygonLayer: activateHotspotPolygonLayerAction,
    deactivateHotspotPolygonLayer: deactivateHotspotPolygonLayerAction,
    deactivateHotspotScanAreaLayer: deactivateHotspotScanAreaLayerAction,
    activateHotspotScanAreaLayer: activateHotspotScanAreaLayerAction,
    deactivateOrthoScanAreaLayer: deactivateOrthoScanAreaLayerAction,
    activateOrthoScanAreaLayer: activateOrthoScanAreaLayerAction,
    activateHotspotPolygonAreaLayer: activateHotspotPolygonAreaLayerAction,
    deactivateHotspotPolygonAreaLayer: deactivateHotspotPolygonAreaLayerAction,
    activateRemovedHotspotsLayer: activateRemovedHotspotsLayerAction,
    deactivateRemovedHotspotsLayer: deactivateRemovedHotspotsLayerAction,
    setHotspotOverlapLayer,
    clearFlightsState,
    starAtlasLiveVideoFlight,
    starManyAtlasLiveVideoFlights,
    unstarAtlasLiveVideoFlight,
    unstarAllAtlasLiveVideoFlights,
} = actions

export default reducer

const root = (state: RootState) => state.entities.flights
export const selectOrthoLayerVisibilities = memoize((flightId: string) =>
    createSelector(
        [root],
        (root) =>
            root.byId[flightId]?.uiContext.showLayers ||
            ({ ...initialLayersUiContext } as typeof initialLayersUiContext)
    )
)

export const selectStarredAtlasLiveVideoFlights = createSelector(
    [root],
    (s) => s.starredAtlasLiveVideoIds
)
